import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueueDequeue } from './QueueDequeueContext';
import { useSettings } from './SettingsContext';
import { undoLastDequeue, withinUndoWindow } from '../services/queue';
import { createPCAgentClient } from '../services/pcAgent';
import { DISPATCH_COOLDOWN_MS, DISPATCH_TRIGGER } from '../config/dispatch';
import { DEV_TOOLS_ENABLED } from '../utils/env';
import { freeCandidates, networkLoad, pickNextPc } from '../services/dispatchPick';
import { fakeAgentStop } from '../services/fakeAgent';
import type { ConfigurationRelease, PCInstance, StartedEntry } from '../types/database';

// Queue-first dispatch: room-level free-PC awareness + the "Volgende bezoeker"
// pop-up lifecycle + undo-with-stop. Mounted inside QueueDequeueProvider (queue
// mode only). Tiles feed it directly:
//  - every PCTile reports {online, running, canLaunch, ...} from its own 8s
//    status poll (no second poller anywhere), and
//  - registers an imperative launch function, so the dispatch modal launches
//    THROUGH the tile's own flow (diagnostics gate, progress bar, failure
//    toasts, post-success dequeue) instead of duplicating it.

export interface PCDispatchReport {
  /** The machine name. Stable, used for sorting and for admin-facing titles. */
  pcName: string;
  /** What the operator actually SEES on this PC's bar: the bare player index
   *  for an operator, "2: MONA-02" for an admin. The queue's pairing must use
   *  this or staff cannot match "Bakker 2 gaat naar ..." to a bar. Falls back
   *  to pcName when a reporter does not set it. */
  pcLabel?: string;
  online: boolean;
  /** An experience is running (or still loading) on this PC. */
  running: boolean;
  /** The tile's composite launch gate (headset/diagnostics/sync/permissions). */
  canLaunch: boolean;
  /** This PC is in crash recovery: the agent is putting the SAME visitor back
   *  (relaunch in flight), or it gave up and that visitor is still unresolved.
   *  Either way the PC is not free for the next person, so the running -> free
   *  transition must not open "Volgende bezoeker". */
  recovering: boolean;
}

/** Launches `release` on the tile's PC. Resolves true on launch success.
 *  `dequeueEntryId` makes the tile's post-success dequeue target exactly the
 *  dispatched person (independent of the widget's dequeue-on-launch toggle). */
export type DispatchLaunchFn = (
  release: ConfigurationRelease,
  language: string | null,
  dequeueEntryId: string,
  /** True only for a pinned MOVE: this visitor is already playing somewhere and
   *  is being re-pointed at this PC on purpose. A normal dispatch leaves it
   *  false so the RPC refuses to move somebody by accident. */
  allowMove?: boolean,
) => Promise<boolean>;

export interface LastDispatch {
  queueEntryId: string;
  departureId: string;
  pcId: string;
  pcName: string;
  /** Operator-facing label for pcId, resolved from that PC's own report. */
  pcLabel: string;
  customerName: string;
  experienceId: string;
  /** UPPERCASE agent code as launched, null when the release has no languages. */
  language: string | null;
  at: number;
}

/** A started visitor the dispatch pop-up is opened FOR (tile recovery:
 *  "Verplaats naar andere PC"). The pop-up shows this visitor instead of the
 *  queue selection, and the other tile's launch moves started_pc_id. */
export interface PinnedVisitor {
  entry: StartedEntry;
  fromPcId: string;
  fromPcName: string;
  /** The operator-facing label for fromPcId; banners print this. */
  fromPcLabel: string;
  /** What was running when the tile stopped it, so the pop-up can preselect it. */
  experienceId: string | null;
  language: string | null;
}

export type UndoOutcome =
  | 'undone_with_stop'   // queue restored + the dispatched experience killed
  | 'undone'             // queue restored; nothing to stop (or different seed)
  | 'nothing';           // nothing to undo within the 5-minute window

/** `needsConfirm`: the row undo would restore is not this operator's own fresh
 *  dispatch (a colleague's start, an admin removal, or unknown). Callers ask
 *  the operator, then call again with `force`. */
export type UndoResultOutcome =
  | UndoOutcome
  | { needsConfirm: true; predicted: StartedEntry | null };

export interface DispatchCtx {
  /** The location both queue providers run on (infra.rooms.location). */
  locationId: string | null;
  /** Live per-PC reports for the current room (tile-fed). */
  pcReports: ReadonlyMap<string, PCDispatchReport>;
  /** The PC the next visitor lands on, by the SAME rule the dispatch pop-up
   *  preselects with (services/dispatchPick). Read it -- never re-derive it,
   *  or the bar starts promising a PC the pop-up will not pick. */
  nextPcId: string | null;
  reportPC: (pcId: string, report: PCDispatchReport) => void;
  retractPC: (pcId: string) => void;
  registerLaunch: (pcId: string, fn: DispatchLaunchFn) => void;
  unregisterLaunch: (pcId: string) => void;
  /** Launch via the registered tile flow; remembers the dispatch for undo. */
  launchOn: (
    pc: { pcId: string; pcName: string },
    release: ConfigurationRelease,
    language: string | null,
    entry: { queueEntryId: string; departureId: string; customerName: string },
    opts?: { allowMove?: boolean },
  ) => Promise<boolean>;
  /** Pop-up state. `preferredPcId` = the PC that just became free, if any. */
  dispatchOpen: boolean;
  preferredPcId: string | null;
  /** Non-null = pinned-visitor mode ("Verplaats bezoeker"). */
  pinnedVisitor: PinnedVisitor | null;
  openDispatch: (preferredPcId?: string | null, opts?: { pinned?: PinnedVisitor | null }) => void;
  closeDispatch: () => void;
  /** A started visitor whose experience was stopped for a move and who has
   *  not been relaunched yet; the NextUpWidget banner keeps them visible. */
  pendingMove: PinnedVisitor | null;
  setPendingMove: (v: PinnedVisitor | null) => void;
  lastDispatch: LastDispatch | null;
  /** Undo the last dequeue at this location; also stops the experience when
   *  it was our own dispatch (or a confirmed started row with a known PC). */
  undoLast: (opts?: { force?: boolean }) => Promise<UndoResultOutcome>;
  /** Stop whatever runs on a room PC (no confirm). Resolves true on success. */
  killOnPc: (pcId: string) => Promise<boolean>;
  /** Headset IPD lock on a room PC: `manual` true = kid mode (the agent stops
   *  holding the headset at 62 mm for this run so the buttons can reach 58),
   *  false = automatic, sent at once. Resolves true when the agent recorded it. */
  setHmdIpdOn: (pcId: string, manual: boolean) => Promise<boolean>;
  /** Ignore the running→free trigger for this PC for `ms`: the tile recovery
   *  flows (restart / move / requeue) free a PC on purpose. */
  suppressAutoOpen: (pcId: string, ms: number) => void;
  /** Which network (router) each room PC hangs off. A room can have several,
   *  and headsets stream over WiFi, so the dispatch pick spreads visitors
   *  across them instead of stacking five on one router. */
  networkOf: (pcId: string) => string | null;
}

const QueueDispatchContext = createContext<DispatchCtx | null>(null);

interface ProviderProps {
  /** Same location the QueueDequeueProvider runs on. */
  locationId: string | null;
  /** Room PCs — used to reach a PC for the undo kill (LAN/Tailscale hosts). */
  pcs: PCInstance[];
  /** Installed releases per PC, so nextPcId can skip a PC that lacks the build
   *  the next visitor is booked for — the same filter the pop-up applies. */
  pcExperiencesMap?: ReadonlyMap<string, { experience_id: string }[]>;
  children: ReactNode;
}

/** Value equality for a tile's status report (all fields are primitives). */
function sameReport(a: PCDispatchReport, b: PCDispatchReport): boolean {
  return (
    a.pcName === b.pcName &&
    a.pcLabel === b.pcLabel &&
    a.online === b.online &&
    a.running === b.running &&
    a.canLaunch === b.canLaunch &&
    a.recovering === b.recovering
  );
}

function currentOperatorId(): string | undefined {
  try {
    const raw = sessionStorage.getItem('current_operator');
    return raw ? (JSON.parse(raw).id as string) : undefined;
  } catch {
    return undefined;
  }
}

export function QueueDispatchProvider({ locationId, pcs, pcExperiencesMap, children }: ProviderProps) {
  const queue = useQueueDequeue();
  const { settings } = useSettings();
  const [pcReports, setPcReports] = useState<Map<string, PCDispatchReport>>(new Map());
  const [dispatchOpen, setDispatchOpen] = useState(false);
  const [preferredPcId, setPreferredPcId] = useState<string | null>(null);
  const [pinnedVisitor, setPinnedVisitor] = useState<PinnedVisitor | null>(null);
  const [pendingMove, setPendingMove] = useState<PinnedVisitor | null>(null);
  const [lastDispatch, setLastDispatch] = useState<LastDispatch | null>(null);

  // Mirror of pcReports for the callbacks that must not re-create on every
  // report (launchOn has an empty dep array on purpose, so reading the state
  // directly there would capture the map as it was at mount).
  const reportsRef = useRef(pcReports);
  useEffect(() => { reportsRef.current = pcReports; }, [pcReports]);

  const launchFns = useRef<Map<string, DispatchLaunchFn>>(new Map());
  const launchInFlight = useRef(false);
  const lastAutoOpenAt = useRef(0);
  const prevReports = useRef<Map<string, PCDispatchReport>>(new Map());
  const suppressedUntil = useRef<Map<string, number>>(new Map());

  // Refs so the trigger logic (called from tile effects) reads fresh values
  // without re-subscribing anything.
  const queueLenRef = useRef(0);
  queueLenRef.current = queue.entries.filter((e) => !e.parked).length;
  const dispatchOpenRef = useRef(dispatchOpen);
  dispatchOpenRef.current = dispatchOpen;
  const promptWhenPcsFreeRef = useRef(true);
  promptWhenPcsFreeRef.current = settings.queuePromptWhenPcsFree;

  const openDispatch = useCallback<DispatchCtx['openDispatch']>((prefer, opts) => {
    setPreferredPcId(prefer ?? null);
    setPinnedVisitor(opts?.pinned ?? null);
    setDispatchOpen(true);
  }, []);

  const closeDispatch = useCallback(() => {
    setDispatchOpen(false);
    setPreferredPcId(null);
    setPinnedVisitor(null);
  }, []);

  // Nothing left to dispatch: close. Both auto-open paths already refuse to
  // OPEN on an empty queue, but the queue can empty while the pop-up is
  // standing — it opens for the head, another operator (or another PC) starts
  // that party, and what is left is a modal saying "de wachtrij is leeg" over a
  // disabled Start button (Dirk 2026-09-16, screenshot). Pinned-visitor mode is
  // exempt: "verplaats bezoeker" carries its own target and does not need a
  // queue at all.
  useEffect(() => {
    if (!dispatchOpen || pinnedVisitor) return;
    if (queue.loading) return;
    if (queue.entries.some((e) => !e.parked)) return;
    closeDispatch();
  }, [dispatchOpen, pinnedVisitor, queue.entries, queue.loading, closeDispatch]);

  // A pending move is resolved elsewhere (the row left 'started': relaunched
  // on another device, requeued, undone) — drop the banner.
  useEffect(() => {
    if (!pendingMove) return;
    const still = queue.startedToday.some(
      (s) => s.queue_entry_id === pendingMove.entry.queue_entry_id,
    );
    if (!still && !queue.loading) setPendingMove(null);
  }, [pendingMove, queue.startedToday, queue.loading]);

  const networkOf = useCallback(
    (pcId: string): string | null => pcs.find((p) => p.id === pcId)?.network_id ?? null,
    [pcs],
  );

  const suppressAutoOpen = useCallback((pcId: string, ms: number) => {
    suppressedUntil.current.set(pcId, Date.now() + ms);
  }, []);

  const maybeAutoOpen = useCallback((freedPcId: string, roomWasFull: boolean) => {
    if (DISPATCH_TRIGGER !== 'pc_free') return;
    // Crash recovery is an independent veto: the PC looks free because the
    // experience died, but the previous visitor is still on it (relaunch in
    // flight) or still unresolved (the agent gave up). Asking for the NEXT
    // visitor here is exactly the bug this block exists to prevent.
    if (prevReports.current.get(freedPcId)?.recovering) return;
    // Device setting: on a quiet moment (another PC was already idle) the
    // operator can start people from the tiles, so the pop-up is optional.
    if (!roomWasFull && !promptWhenPcsFreeRef.current) return;
    if ((suppressedUntil.current.get(freedPcId) ?? 0) > Date.now()) return;
    if (queueLenRef.current === 0) return;
    if (dispatchOpenRef.current || launchInFlight.current) return;
    const now = Date.now();
    if (now - lastAutoOpenAt.current < DISPATCH_COOLDOWN_MS) return;
    lastAutoOpenAt.current = now;
    openDispatch(freedPcId);
  }, [openDispatch]);

  // "Was the room full?" — every OTHER online PC busy at the moment this one
  // freed up. Read before the new report is stored, so the answer describes
  // the situation the operator was in, not the one they are in now.
  const roomWasFull = useCallback((freedPcId: string): boolean => {
    for (const [id, r] of prevReports.current) {
      if (id === freedPcId) continue;
      if (r.online && !r.running) return false;
    }
    return true;
  }, []);

  const reportPC = useCallback((pcId: string, report: PCDispatchReport) => {
    const prev = prevReports.current.get(pcId);
    // Every tile calls this from its own 8s status poll with a freshly built
    // object, and an idle room reports the same six fields forever. Writing
    // state anyway replaced the map, replaced the context value, and re-rendered
    // every consumer (all tiles, the queue column, the header) several times a
    // minute for no new information. An unchanged report also cannot be a
    // running -> free transition, so there is nothing below to run either.
    if (prev && sameReport(prev, report)) return;
    const wasFull = roomWasFull(pcId);
    prevReports.current.set(pcId, report);
    setPcReports((m) => {
      const next = new Map(m);
      next.set(pcId, report);
      return next;
    });
    // Trigger: running → free while online. A tile appearing already-free
    // (mount, room switch) is NOT a trigger — only a genuine transition is,
    // and a PC that freed up because its experience CRASHED is not free at
    // all: the crashed visitor is still started on it.
    if (prev && prev.running && !report.running && report.online && !report.recovering) {
      maybeAutoOpen(pcId, wasFull);
    }
  }, [maybeAutoOpen, roomWasFull]);

  const retractPC = useCallback((pcId: string) => {
    prevReports.current.delete(pcId);
    setPcReports((m) => {
      if (!m.has(pcId)) return m;
      const next = new Map(m);
      next.delete(pcId);
      return next;
    });
  }, []);

  const registerLaunch = useCallback((pcId: string, fn: DispatchLaunchFn) => {
    launchFns.current.set(pcId, fn);
  }, []);
  const unregisterLaunch = useCallback((pcId: string) => {
    launchFns.current.delete(pcId);
  }, []);

  const launchOn = useCallback<DispatchCtx['launchOn']>(
    async (pc, release, language, entry, opts) => {
      const fn = launchFns.current.get(pc.pcId);
      if (!fn) return false;
      launchInFlight.current = true;
      try {
        const ok = await fn(release, language, entry.queueEntryId, opts?.allowMove === true);
        if (ok) {
          setLastDispatch({
            queueEntryId: entry.queueEntryId,
            departureId: entry.departureId,
            pcId: pc.pcId,
            pcName: pc.pcName,
            // The bar's label, not the machine name: the undo pill has to name
            // the PC the way the operator sees it on screen.
            pcLabel: reportsRef.current.get(pc.pcId)?.pcLabel ?? pc.pcName,
            customerName: entry.customerName,
            experienceId: release.experience_id,
            language,
            at: Date.now(),
          });
        }
        return ok;
      } finally {
        launchInFlight.current = false;
      }
    },
    [],
  );

  const killOnPc = useCallback(async (pcId: string): Promise<boolean> => {
    // Staging fake tile (components/FakePCTile): stop the in-memory agent so
    // undo-with-stop behaves on a simulated PC too. No-op for real PCs.
    if (DEV_TOOLS_ENABLED && fakeAgentStop(pcId)) return true;
    const pc = pcs.find((p) => p.id === pcId);
    if (!pc) return false;
    try {
      const client = createPCAgentClient(
        [pc.ip_address, pc.tailscale_ip].filter(Boolean) as string[],
        9090,
        pc.id,
      );
      const res = await client.killExperience(false, currentOperatorId());
      return !!res?.success;
    } catch (err) {
      console.error('killOnPc failed', err);
      return false;
    }
  }, [pcs]);

  const setHmdIpdOn = useCallback(async (pcId: string, manual: boolean): Promise<boolean> => {
    const pc = pcs.find((p) => p.id === pcId);
    // A staging fake tile has no agent behind it; pretend it took the flag.
    if (!pc) return DEV_TOOLS_ENABLED;
    try {
      const client = createPCAgentClient(
        [pc.ip_address, pc.tailscale_ip].filter(Boolean) as string[],
        9090,
        pc.id,
      );
      const res = await client.setHmdIpd({ manual, operator: currentOperatorId() });
      // The flag is recorded even when the immediate send failed (headset
      // still booting): only a reply WITHOUT state means the agent did not take it.
      return !!res?.data;
    } catch (err) {
      console.error('setHmdIpdOn failed', err);
      return false;
    }
  }, [pcs]);

  const undoLast = useCallback<DispatchCtx['undoLast']>(async (opts) => {
    const operatorId = currentOperatorId();
    if (!locationId || !operatorId) return 'nothing';

    // Predict the seed the RPC will pick (most recent event at the location
    // within 5 min; removals are not in startedToday, so the prediction can
    // be null while a removal is still undoable). Only our own fresh dispatch
    // is undone without asking: anything else may be a colleague's action.
    const predicted =
      queue.latestDequeue && withinUndoWindow(queue.latestDequeue.last_event_at)
        ? queue.latestDequeue
        : null;
    const ownFresh = lastDispatch && withinUndoWindow(lastDispatch.at) ? lastDispatch : null;
    const isOwn = !!ownFresh && (!predicted || predicted.queue_entry_id === ownFresh.queueEntryId);
    if (!isOwn && !opts?.force) {
      return { needsConfirm: true, predicted };
    }

    let result;
    try {
      result = await undoLastDequeue(locationId, operatorId);
    } catch (err) {
      const msg = (err as { message?: string })?.message ?? '';
      if (msg.includes('nothing_to_undo')) return 'nothing';
      throw err;
    }
    queue.refresh();
    if (pendingMove && pendingMove.entry.queue_entry_id === result.seed_entry_id) {
      setPendingMove(null);
    }

    // Stop the experience when the restored entry is the one WE dispatched.
    // After an explicit confirm (force) a started seed with a known PC is
    // stopped too: the operator just agreed to put that visitor back. An
    // admin-side removal (was_state removed, no PC) never kills anything.
    let killPcId: string | null = null;
    if (ownFresh && result.seed_entry_id === ownFresh.queueEntryId) {
      killPcId = ownFresh.pcId;
      setLastDispatch(null);
    } else if (opts?.force && result.was_state === 'started' && result.seed_pc_id) {
      killPcId = result.seed_pc_id;
    }
    if (killPcId) {
      const stopped = await killOnPc(killPcId);
      return stopped ? 'undone_with_stop' : 'undone';
    }
    return 'undone';
  }, [locationId, queue, lastDispatch, pendingMove, killOnPc]);

  // 'bijna_klaar' policy: the tile already broadcasts a next-up CustomEvent at
  // the agent's stage-1 notification — reuse it as the auto-open signal.
  useEffect(() => {
    if (DISPATCH_TRIGGER !== 'bijna_klaar') return;
    const onNextUp = () => {
      if (queueLenRef.current === 0) return;
      if (dispatchOpenRef.current || launchInFlight.current) return;
      const now = Date.now();
      if (now - lastAutoOpenAt.current < DISPATCH_COOLDOWN_MS) return;
      lastAutoOpenAt.current = now;
      openDispatch(null);
    };
    window.addEventListener('reality-operator:next-up', onNextUp);
    return () => window.removeEventListener('reality-operator:next-up', onNextUp);
  }, [openDispatch]);

  // Dev/staging tools (DEV_TOOLS_ENABLED): inject fake PC reports from the
  // console to exercise auto-select with more PCs than staging physically has:
  //   window.dispatchDebug.report('fake-1', { pcName: 'FAKE-1', online: true, running: false, canLaunch: true })
  //   window.dispatchDebug.free('fake-1')   // simulate a running→free transition
  // Visible fake tiles (Settings -> Staging tools -> "Fake PC tiles") are
  // ordinary tiles from the provider's point of view: components/FakePCTile
  // reports and registers exactly like PCTile does.
  useEffect(() => {
    if (!DEV_TOOLS_ENABLED) return;
    const w = window as unknown as Record<string, unknown>;
    w.dispatchDebug = {
      report: (pcId: string, r: Partial<PCDispatchReport>) =>
        reportPC(pcId, {
          pcName: r.pcName ?? pcId,
          online: r.online ?? true,
          running: r.running ?? false,
          canLaunch: r.canLaunch ?? true,
          recovering: r.recovering ?? false,
        }),
      free: (pcId: string, opts?: { recovering?: boolean }) => {
        const base = { pcName: pcId, online: true, canLaunch: true, recovering: false };
        reportPC(pcId, { ...base, running: true });
        setTimeout(
          () => reportPC(pcId, { ...base, running: false, recovering: !!opts?.recovering }),
          50,
        );
      },
      retract: (pcId: string) => retractPC(pcId),
    };
    return () => {
      delete w.dispatchDebug;
    };
  }, [reportPC, retractPC]);

  // THE answer to "which PC is next". Everything that names one reads this:
  // the bar's "Vrij -> Bakker 2", the wachtrij, and the pop-up's preselection.
  // Three separate derivations used to disagree (Dirk 2026-09-16).
  const nextPcId = useMemo(() => {
    const bookedIds = (queue.headEntry?.experiences ?? []).map((p) => p.experience_id);
    const hasExperience = (pcId: string) => {
      if (!pcExperiencesMap || bookedIds.length === 0) return true;
      const installed = pcExperiencesMap.get(pcId) ?? [];
      return bookedIds.some((id) => installed.some((rel) => rel.experience_id === id));
    };
    const load = networkLoad(pcReports, networkOf);
    return pickNextPc({
      candidates: freeCandidates(pcReports, hasExperience),
      networkLoadOf: (pcId) => load.get(networkOf(pcId) ?? 'unknown') ?? 0,
      lastUsedAt: (pcId) => {
        const started = queue.startedByPc.get(pcId);
        if (!started) return 0;
        const t = Date.parse(started.last_event_at ?? started.started_at);
        return Number.isFinite(t) ? t : 0;
      },
    });
  }, [pcReports, pcExperiencesMap, networkOf, queue.headEntry, queue.startedByPc]);

  const value = useMemo<DispatchCtx>(
    () => ({
      locationId,
      pcReports,
      nextPcId,
      reportPC,
      retractPC,
      registerLaunch,
      unregisterLaunch,
      launchOn,
      dispatchOpen,
      preferredPcId,
      pinnedVisitor,
      openDispatch,
      closeDispatch,
      pendingMove,
      setPendingMove,
      lastDispatch,
      undoLast,
      killOnPc,
      setHmdIpdOn,
      suppressAutoOpen,
      networkOf,
    }),
    [locationId, pcReports, nextPcId, reportPC, retractPC, registerLaunch, unregisterLaunch, launchOn,
     dispatchOpen, preferredPcId, pinnedVisitor, openDispatch, closeDispatch, pendingMove,
     lastDispatch, undoLast, killOnPc, setHmdIpdOn, suppressAutoOpen],
  );

  return (
    <QueueDispatchContext.Provider value={value}>
      {children}
    </QueueDispatchContext.Provider>
  );
}

export function useQueueDispatch(): DispatchCtx {
  const v = useContext(QueueDispatchContext);
  if (!v) throw new Error('useQueueDispatch must be used inside <QueueDispatchProvider>');
  return v;
}

/** Null when queue mode is off (provider absent) — PCTile uses this so it
 *  renders identically on QueueDisplay and the legacy dashboard. */
export function useQueueDispatchOptional(): DispatchCtx | null {
  return useContext(QueueDispatchContext);
}
